import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useVoiceSettings } from '@/hooks/useVoiceSettings';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Building2,
  Briefcase,
  Calendar,
  Wallet,
  IndianRupee,
  Pencil,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { staffService, expenseService, walletService, Staff, Expense } from '@/lib/storage';
import ExpenseModal from '@/components/ExpenseModal';
import { cn } from '@/lib/utils';

const statusConfig: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { label: 'Rejected', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
};

const StaffProfilePage = () => {
  const { session } = useAuth();
  const { settings: voiceSettings, toggleEnabled: toggleVoice } = useVoiceSettings();
  const [staff, setStaff] = useState<Staff | undefined>(undefined);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [editExpense, setEditExpense] = useState<Expense | null>(null);

  const loadData = async (silent = false) => {
    if (session?.userId) {
      if (!silent) setLoading(true);
      try {
        const [staffData, expenseData, walletData] = await Promise.all([
          staffService.getById(session.userId),
          expenseService.getAll({ staffId: session.userId }),
          walletService.getBalance(session.userId),
        ]);
        setStaff(staffData);
        setExpenses(expenseData);
        setWalletBalance(walletData.balance);
      } catch (error) {
        console.error("Failed to load data", error);
      } finally {
        if (!silent) setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 10000);
    return () => clearInterval(interval);
  }, [session?.userId]);

  const handleExpenseUpdated = async () => {
    if (session?.userId) {
      const [expenseData, walletData] = await Promise.all([
        expenseService.getAll({ staffId: session.userId }),
        walletService.getBalance(session.userId),
      ]);
      setExpenses(expenseData);
      setWalletBalance(walletData.balance);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <span className="w-9 h-9 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-semibold text-slate-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-sm font-semibold text-slate-400">Profile data could not be retrieved.</p>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  const pendingExpenses = expenses.filter(e => e.status === 'pending');
  const totalApproved = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="max-w-2xl mx-auto space-y-6"
    >
      {/* Profile Header */}
      <motion.div variants={itemVariants}>
        <GlassCard className="text-center py-10 border-slate-200/50 dark:border-slate-800/40 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500" />
          
          <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-emerald-500/10 to-teal-500/10 dark:from-emerald-500/20 dark:to-teal-500/20 border-2 border-emerald-500/20 flex items-center justify-center mx-auto mb-4.5 shadow-sm">
            <span className="text-emerald-600 dark:text-emerald-400 font-black text-4xl leading-none">
              {staff.name.charAt(0).toUpperCase()}
            </span>
          </div>
          
          <h1 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">{staff.name}</h1>
          <p className="text-sm text-slate-500 font-semibold mt-1">{staff.email}</p>
          
          <div className="flex items-center justify-center gap-2 mt-4">
            <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-350 border-none font-bold rounded-lg px-2.5 py-0.5 text-[10px]">
              {staff.department}
            </Badge>
            <Badge className="bg-indigo-500/5 text-indigo-600 dark:text-indigo-400 border border-indigo-200/10 font-bold rounded-lg px-2.5 py-0.5 text-[10px]">
              {staff.position}
            </Badge>
          </div>

          <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 dark:border-amber-800/30">
            <Wallet className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-bold text-amber-700 dark:text-amber-400">
              Wallet Balance: ₹{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="mt-4 flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50">
            <div className="flex items-center gap-2">
              {voiceSettings.enabled ? (
                <Volume2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <VolumeX className="w-4 h-4 text-slate-400" />
              )}
              <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Sound Effects</span>
            </div>
            <button
              onClick={toggleVoice}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${voiceSettings.enabled ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${voiceSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </GlassCard>
      </motion.div>

      {/* Profile Details */}
      <motion.div variants={itemVariants}>
        <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
          <CardHeader className="pb-3 pt-6 px-6 border-b border-slate-100 dark:border-slate-800/40">
            <CardTitle className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-500" />
              Member Profile Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {[
              { label: 'Registered Email', value: staff.email, icon: Mail, color: 'text-blue-500' },
              { label: 'Assigned Department', value: staff.department, icon: Building2, color: 'text-indigo-500' },
              { label: 'Work Designation / Level', value: staff.position, icon: Briefcase, color: 'text-emerald-500' },
              { label: 'Total Approved Expenses', value: `₹${totalApproved.toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-amber-500' },
              {
                label: 'Onboarding Date',
                value: new Date(staff.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
                icon: Calendar,
                color: 'text-amber-500'
              }
            ].map(row => (
              <div key={row.label} className="flex items-center gap-4 p-3.5 rounded-2xl border border-slate-150 dark:border-slate-850 bg-slate-50/50 dark:bg-slate-950/20">
                <div className={`w-10 h-10 rounded-xl bg-slate-100/70 dark:bg-slate-900/50 border border-slate-200/30 dark:border-slate-800/30 flex items-center justify-center shrink-0 ${row.color}`}>
                  <row.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-400">{row.label}</p>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-250 mt-0.5">{row.value}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </GlassCard>
      </motion.div>

      {/* Expense History */}
      <motion.div variants={itemVariants}>
        <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
          <CardHeader className="pb-3 pt-6 px-6 border-b border-slate-100 dark:border-slate-800/40">
            <CardTitle className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-amber-500" />
              My Expenses
              {pendingExpenses.length > 0 && (
                <span className="ml-auto text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-bold">
                  {pendingExpenses.length} pending
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {expenses.length === 0 ? (
              <div className="flex flex-col items-center py-6 text-muted-foreground">
                <Wallet className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-sm font-medium">No expenses yet</p>
                <p className="text-xs mt-1">Expenses submitted after checkout will appear here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {expenses.map((exp) => (
                  <div
                    key={exp.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800/40 bg-slate-50/50 dark:bg-slate-950/20"
                  >
                    <div className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                      exp.status === 'approved' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                      exp.status === 'rejected' ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400' :
                      'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                    )}>
                      {exp.status === 'approved' ? <CheckCircle2 className="w-4 h-4" /> :
                       exp.status === 'rejected' ? <XCircle className="w-4 h-4" /> :
                       <Clock className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">₹{exp.amount.toLocaleString('en-IN')}</span>
                        <span className={cn(
                          'text-[10px] px-1.5 py-0.5 rounded-full font-semibold',
                          statusConfig[exp.status]?.className
                        )}>
                          {statusConfig[exp.status]?.label}
                        </span>
                      </div>
                      {exp.remark && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{exp.remark}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(exp.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    {exp.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 shrink-0"
                        onClick={() => setEditExpense(exp)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </GlassCard>
      </motion.div>

      <ExpenseModal
        open={!!editExpense}
        onClose={() => { setEditExpense(null); handleExpenseUpdated(); }}
        expense={editExpense}
      />
    </motion.div>
  );
};

export default StaffProfilePage;
